import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
} from '@chakra-ui/react';
import { Button, SelectMenu, showToast } from '@spotoninc/nexus-react';
import { fetchWithAuth } from '../../utils/apiHelpers';

import '../../styles/reskinned-integration-dropdown.css';
import '../../styles/reskinned-location-dropdown.css';
import { downloadExcel } from 'react-export-table-to-excel';
import ChakraUploader from '../common/ChakraUploader';
import CustomLocationSelector from '../utility/customLocationSelector';
import '../../styles/content-fit-dropdown.css';
// import BudgetImage from '../../media/images/BudgetImage.svg';

/**
 * ManageBudget Component
 *
 * This component allows users to manage budget data by:
 * 1. Downloading budget templates based on selected criteria
 * 2. Uploading budget data files
 */

const ManageBudget = ({ locationValue, setLocation }) => {
  // State variables
  const [locationDisable, setLocationDisable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formChanged, setFormChanged] = useState(false);
  const [type, setType] = useState(undefined);
  const [year, setYear] = useState(undefined);
  const [selectedIntegration, setSelectedIntegration] = useState(undefined);
  const [company_integration, setCompanyIntegration] = useState(undefined);
  const [integration_location, setIntegrationLocation] = useState(undefined);
  const [years, setYears] = useState(undefined);
  const [budgetData, setBudgetData] = useState(undefined);
  // Constants
  const types = [
    { value: 1, label: 'Revenue' },
    { value: 2, label: 'Expense' },
  ];

  // Fetch initial data on component mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const response = await fetchWithAuth(
        '/api/fetch_budget_settings_dd/?' +
          new URLSearchParams({
            initial_load: '1',
          }),
        {
          method: 'GET',
        }
      );

      const data = await response.json();

      setCompanyIntegration(data['company_integration']);
      setIntegrationLocation(data['integration_location']);
      setYears(data['years']);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBudgetConfiguration = async () => {
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('integration_id', selectedIntegration);

      //   if (location_id !== undefined) {
      //     formData.append('location_id', location_id);
      //   }

      formData.append('year', year);
      formData.append('budget_type', type);
      formData.append('type', 'download');

      const response = await fetchWithAuth('/api/fetch_budget_settings_dd/', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      setBudgetData(data);
      handleDownloadExcel(data);
      showToast({
        priority: 'success',
        content: 'Downloaded successfully',
        autoClose: 5000,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadExcel = data => {
    downloadExcel({
      fileName: 'Budget Configuration',
      sheet: 'summary',
      tablePayload: {
        header: Object.keys(data[0]),
        body: data,
      },
    });
  };

  const handleIntegrationChange = (e, value) => {
    const integration = company_integration?.find(
      item => item.integration_id === value
    );

    if (integration) {
      setLocationDisable(!integration.location_flag);
      setSelectedIntegration(integration.integration_id);
      setFormChanged(true);
    }
  };

  const handleYearChange = (e, value) => {
    setYear(value);
    setFormChanged(true);
  };

  const handleTypeChange = (e, value) => {
    const selectedType = types.find(item => item.value === value);

    setType(value);
    setFormChanged(true);
  };

  const handleFileUpload = async files => {
    // Return a promise that resolves when the upload is complete
    return new Promise(async (resolve, reject) => {
      // Check if required fields are selected
      if (!selectedIntegration || !year || !type) {
        showToast({
          priority: 'error',
          content: 'Please select Integration, Year, and Type before uploading',
          autoClose: 5000,
        });
        return reject(new Error('Missing required fields'));
      }

      try {
        const file = files[0];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('integration_id', selectedIntegration);

        // Add location_id if available
        if (locationValue) {
          formData.append('location_id', locationValue);
        }

        formData.append('type', 'upload');
        formData.append('budget_type', type);
        formData.append('year', year);

        const response = await fetchWithAuth('/api/fetch_budget_settings_dd/', {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          showToast({
            priority: 'success',
            content: 'File uploaded successfully!',
            autoClose: 5000,
          });
          resolve(); // Resolve the promise on success
        } else {
          const errorText = await response.text();
          console.error(
            'Server response:',
            errorText,
            'Status:',
            response.status
          );
          throw new Error(
            `Server error (${response.status}): Please check file format and try again`
          );
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        showToast({
          priority: 'error',
          content: `Error uploading file: ${error.message || 'Please check the file before uploading.'}`,
          autoClose: 5000,
        });
        reject(error); // Reject the promise on error
      }
    });
  };

  // Transform data for SelectMenu components
  const integrationOptions =
    company_integration?.map(item => ({
      value: item.integration_id,
      label: item.integration_name,
    })) || [];

  const yearOptions =
    years?.map(year => ({
      value: year,
      label: year.toString(),
    })) || [];

  // State for form validation
  const [isFormValid, setIsFormValid] = useState(false);

  // Update form validation whenever relevant fields change
  useEffect(() => {
    const valid =
      selectedIntegration !== undefined &&
      year !== undefined &&
      type !== undefined &&
      !!locationValue;

    setIsFormValid(valid);
  }, [selectedIntegration, year, type, locationDisable, locationValue]);

  return (
    <Box p={4}>
      <Flex mb={6} alignItems="center" gap={4}>
        <Heading as="h1" size="lg">
          Update Budget
        </Heading>
      </Flex>

      <Card className="card-no-border">
        <CardHeader>
          {/* Main container with responsive direction */}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'center', md: 'flex-end' }}
            gap="16px"
            width="100%"
          >
            {/* Left side - Dropdowns container */}
            <Flex
              direction={{ base: 'column', md: 'row' }}
              gap="16px"
              flex="1"
              wrap={{ base: 'nowrap', md: 'nowrap' }}
              width={{ base: '100%', md: 'auto' }}
              alignItems="center"
            >
              {/* Integration Dropdown */}
              <SelectMenu
                placeholder="Select Integration"
                options={integrationOptions}
                onChange={handleIntegrationChange}
                style={{
                  width: { base: '100%', md: '200px' },
                  height: '46px',
                  minWidth: { base: '100%', md: '200px' },
                  borderColor:
                    !selectedIntegration && formChanged ? 'red.300' : undefined,
                }}
                className="reskinned-location-dropdown content-fit-dropdown location-dropdown"
              />

              {/* Location Dropdown */}
              <Box className="custom-location-selector">
                <CustomLocationSelector
                  locationValue={locationValue}
                  setLocation={setLocation}
                  height="46px"
                  isDisabled={locationDisable || !selectedIntegration}
                />
              </Box>

              {/* Year Dropdown */}
              <SelectMenu
                placeholder="Select Year"
                options={yearOptions}
                onChange={handleYearChange}
                style={{
                  width: { base: '100%', md: '120px' },
                  height: '46px',
                  minWidth: { base: '100%', md: '120px' },
                  borderColor: !year && formChanged ? 'red.300' : undefined,
                }}
                className="reskinned-location-dropdown content-fit-dropdown location-dropdown"
              />

              {/* Type Dropdown */}
              <SelectMenu
                placeholder="Select Type"
                options={types}
                onChange={handleTypeChange}
                style={{
                  width: { base: '100%', md: '150px' },
                  height: '46px',
                  minWidth: { base: '100%', md: '150px' },
                  borderColor: !type && formChanged ? 'red.300' : undefined,
                }}
                className="reskinned-location-dropdown content-fit-dropdown location-dropdown"
              />
            </Flex>

            {/* Right side - Buttons container */}
            <Flex
              direction={{ base: 'row', md: 'row' }}
              gap="16px"
              justify={{ base: 'center', md: 'flex-end' }}
              width={{ base: '100%', md: 'auto' }}
            >
              <Button
                variant="filled"
                isDisabled={!isFormValid}
                isLoading={isLoading}
                onClick={fetchBudgetConfiguration}
              >
                Download
              </Button>
            </Flex>
          </Flex>
        </CardHeader>

        <CardBody py={6}>
          <Flex justifyContent="center" width="100%">
            <ChakraUploader
              onFileAccepted={handleFileUpload}
              fileFormat=".xlsx, .xls, .csv"
              sizeConstraint="Max 5MB"
              accept={{
                'application/vnd.ms-excel': ['.xls'],
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                  ['.xlsx'],
                'text/csv': ['.csv'],
              }}
              maxSize={5242880} // 5MB
              multiple={false}
              height="120px"
            />
          </Flex>
        </CardBody>
      </Card>
    </Box>
  );
};

export default ManageBudget;
